import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, TrendingUp, Calendar, Zap, ArrowUpRight } from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';

const UsageDashboard = ({ className = "" }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await subscriptionService.getCurrentUsage();
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const getUsageStatus = (percentage) => {
    if (percentage >= 100) return { status: 'exceeded', color: 'red', icon: AlertCircle };
    if (percentage >= 80) return { status: 'warning', color: 'yellow', icon: AlertCircle };
    if (percentage >= 60) return { status: 'moderate', color: 'blue', icon: TrendingUp };
    return { status: 'good', color: 'green', icon: Zap };
  };

  if (loading) {
    return (
      <div className={`usage-dashboard ${className}`}>
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`usage-dashboard ${className}`}>
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-600">Unable to load usage data</p>
          </div>
        </div>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  const usagePercentage = usage.usage_percentage || 0;
  const statusInfo = getUsageStatus(usagePercentage);
  const StatusIcon = statusInfo.icon;

  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-600',
      progress: 'bg-red-500'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-600',
      progress: 'bg-yellow-500'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      progress: 'bg-blue-500'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-600',
      progress: 'bg-green-500'
    }
  };

  const colors = colorClasses[statusInfo.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`usage-dashboard ${className}`}
    >
      <div className={`p-3 sm:p-4 rounded-lg border ${colors.bg} ${colors.border}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded-lg ${colors.bg}`}>
              <Calendar className={`w-4 h-4 ${colors.text}`} />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-900">Itinerary Usage</h3>
              <p className="text-[10px] text-gray-600">
                Current plan: <span className="font-medium capitalize">{usage.plan_name}</span>
              </p>
            </div>
          </div>
          <StatusIcon className={`w-4 h-4 ${colors.text}`} />
        </div>

        <div className="mb-2">
          <div className="flex justify-between items-baseline mb-1.5">
            <span className="text-xl font-bold text-gray-900">
              {usage.monthly_itineraries}
              <span className="text-xs font-normal text-gray-600 ml-1">
                / {usage.monthly_limit === -1 ? '∞' : usage.monthly_limit}
              </span>
            </span>
            <span className={`text-xs font-medium ${colors.text}`}>
              {usage.monthly_limit === -1 ? 'Unlimited' : `${Math.round(usagePercentage)}%`}
            </span>
          </div>
          
          {usage.monthly_limit !== -1 && (
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(usagePercentage, 100)}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`h-1.5 rounded-full ${colors.progress}`}
              />
            </div>
          )}
        </div>

        <p className="text-[10px] text-gray-600">
          {usage.can_create ? (
            `You can create ${usage.monthly_limit === -1 ? 'unlimited' : usage.monthly_limit - usage.monthly_itineraries} more itineraries this month`
          ) : (
            'Monthly limit reached. Upgrade to create more itineraries.'
          )}
        </p>

        {usagePercentage >= 80 && usage.monthly_limit !== -1 && (
          <button
            onClick={() => window.location.href = '/pricing'}
            className="mt-2 w-full flex items-center justify-center space-x-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Upgrade Plan</span>
            <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default UsageDashboard;

